import React from 'react';
import TableStyles from '../documents/Table.module.css';
import { useParams, useLocation, useNavigate } from 'react-router';
import useGetPackager from '../hooks/packager/useGetPackager';
import { Typography, Chip, LinearProgress, CircularProgress, Tooltip, Button } from '@mui/material';
import IconButtonWhiteBg from '../common/IconButtonWhiteBg';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAtom } from 'jotai';
import { showPackagerConfigAtom } from '../../atoms/packager-atoms';
import { AppContext } from '../../App';
import {
  DivFlexHeader,
  DivFlexHeaderIcons,
  DivVerticalDividerLarge,
  DivVerticalDividerSmall,
  TypographyPropertiesTitleStyled,
  TypographyPropertiesDataStyled,
} from '../common/StyledComponents';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useCreatePackage } from '../hooks/packages/useCreatePackage';
import { btnSubtlePrimarySx } from '../common/filterStyles';

import {
  PackagerScheduleTypes,
  DeliveryScheduleTypes,
  PackagerStatuses,
} from '../../types/packager-types';
import { useQueryClient } from '@tanstack/react-query';
import { describeCronSchedule } from '../../utils/cronNextRun';
import { formatDistanceToNow, format, parseISO } from 'date-fns';
import PackagerChainOfCustodyReport from './actions/PackagerChainOfCustodyReport';

const PACKAGER_STATUS_COLORS: Record<
  PackagerStatuses,
  { bg: string; color: string }
> = {
  [PackagerStatuses.CREATED]: { bg: '#DBDEE0', color: '#112233' },
  [PackagerStatuses.PROCESSING]: { bg: '#E0F1F7', color: '#32A0CB' },
  [PackagerStatuses.PROCESSING_SUCCESSFUL]: { bg: '#e8f7e8', color: '#43a047' },
  [PackagerStatuses.PROCESSING_FAILED]: { bg: '#fdeaea', color: '#e53935' },
  [PackagerStatuses.TRANSMISSION_SUCCESSFUL]: { bg: '#e8f7e8', color: '#43a047' },
  [PackagerStatuses.TRANSMISSION_FAILED]: { bg: '#fdeaea', color: '#e53935' },
};

const PackageHeader: React.FC = () => {
  const { setToast } = React.useContext(AppContext);
  const { dpid } = useParams<{ dpid: string }>();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { packager, isLoading, isError } = useGetPackager(dpid!);
  const [, setShowPackagerConfig] = useAtom(showPackagerConfigAtom);
  const queryClient = useQueryClient();
  if (isLoading || !packager) {
    return <LinearProgress />;
  }

  if (isError) {
    return <div>Error loading packager data</div>;
  }

  const { mutate: createPackage, isPending: isLoadingCreatePackage } = useCreatePackage({
    onSuccess: (data) => {
      console.log('Package created successfully:', data);
      queryClient.invalidateQueries({
        queryKey: ['packages']
      });
      queryClient.invalidateQueries({
        queryKey: ['packager']
      });
      setToast({
        variant: 'success',
        open: true,
        message: 'Package created successfully!',
      });
    },
    onError: (error) => {
      console.error('Error creating package:', error);
      setToast({
        variant: 'error',
        open: true,
        message: `Failed to create package: ${error.message}`,
      });
    },
  });

  const statusColors = packager?.packager_status
    ? PACKAGER_STATUS_COLORS[packager.packager_status] || { bg: '#ececec', color: '#757575' }
    : { bg: '#ececec', color: '#757575' };

  return (
    <div style={{ borderBottom: '1px solid #BDC3D2', padding: '15px 20px' }}>
      <DivFlexHeader noMargin>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Typography
            variant="h5"
            component="span"
            className={TableStyles.documentsPageTitle}
            data-cy="packager-label"
          >
            {packager?.label || ''}
          </Typography>
          {packager?.packager_status && (
            <Chip
              label={packager.packager_status.replace(/_/g, ' ')}
              size="small"
              data-cy="packager-status-chip"
              sx={{
                backgroundColor: statusColors.bg,
                color: statusColors.color,
                fontWeight: 600,
                fontSize: '11px',
                height: '22px',
              }}
            />
          )}
        </div>

        <DivFlexHeaderIcons>
          <div>
            <TypographyPropertiesTitleStyled>Packager ID</TypographyPropertiesTitleStyled>
            <Tooltip title={'Copy'} placement="top" arrow>
              <span
                onClick={() => {
                  navigator.clipboard.writeText(packager.id || '');
                  setToast({ open: true, message: 'Copied!', variant: 'success' });
                }}
                style={{ cursor: 'pointer' }}
              >
                <TypographyPropertiesDataStyled>{packager?.id}</TypographyPropertiesDataStyled>
              </span>
            </Tooltip>
          </div>
          <DivVerticalDividerSmall />
          <div>
            <TypographyPropertiesTitleStyled>Packager Schedule</TypographyPropertiesTitleStyled>
            {packager?.packager_schedule_type === PackagerScheduleTypes.CRON_SCHEDULE ? (
              <Tooltip
                title={
                  packager?.schedule_cron_expression
                    ? describeCronSchedule(packager.schedule_cron_expression)
                    : ''
                }
                placement="top"
                arrow
              >
                <TypographyPropertiesDataStyled style={{ cursor: 'help' }}>
                  {packager?.packager_schedule_type?.toUpperCase()}
                </TypographyPropertiesDataStyled>
              </Tooltip>
            ) : (
              <TypographyPropertiesDataStyled>
                {packager?.packager_schedule_type?.toUpperCase()}
              </TypographyPropertiesDataStyled>
            )}
          </div>

          <DivVerticalDividerLarge />

          <div>
            <TypographyPropertiesTitleStyled>Delivery Schedule</TypographyPropertiesTitleStyled>
            <Tooltip
              title={
                packager?.delivery_schedule_type === DeliveryScheduleTypes.CRON_SCHEDULE &&
                packager?.delivery_cron_expression
                  ? describeCronSchedule(packager.delivery_cron_expression)
                  : ''
              }
              placement="top"
              arrow
            >
              <TypographyPropertiesDataStyled style={{ cursor: 'help' }}>
                {packager?.delivery_schedule_type?.toUpperCase()}
              </TypographyPropertiesDataStyled>
            </Tooltip>
          </div>

          <DivVerticalDividerLarge />

          <div>
            <TypographyPropertiesTitleStyled>Packager Last Ran</TypographyPropertiesTitleStyled>
            {packager?.last_run ? (
              <Tooltip
                title={format(parseISO(packager.last_run), 'yyyy-MM-dd HH:mm:ss')}
                placement="top"
                arrow
              >
                <TypographyPropertiesDataStyled style={{ cursor: 'help' }}>
                  {formatDistanceToNow(parseISO(packager.last_run), { addSuffix: true })}
                </TypographyPropertiesDataStyled>
              </Tooltip>
            ) : (
              <TypographyPropertiesDataStyled>Never</TypographyPropertiesDataStyled>
            )}
          </div>

          <DivVerticalDividerLarge />

          <PackagerChainOfCustodyReport packagerId={packager?.id!} />

          <DivVerticalDividerLarge />
          <>
            <Button
              variant="outlined"
              onClick={() => createPackage(packager.id!)}
              id={`btn-packager-create`}
              disabled={isLoadingCreatePackage}
              sx={btnSubtlePrimarySx}
              startIcon={
                isLoadingCreatePackage ? (
                  <CircularProgress size={14} />
                ) : (
                  <AddCircleOutlineIcon sx={{ fontSize: 16 }} />
                )
              }
            >
              {isLoadingCreatePackage ? 'Creating...' : 'Create Package'}
            </Button>
            <DivVerticalDividerLarge />{' '}
          </>
          <IconButtonWhiteBg
            disabled={false}
            onClick={() => {
              const params = new URLSearchParams(search);
              params.set('step', '1');
              navigate(`?${params.toString()}`, { replace: true });
              setShowPackagerConfig(true);
            }}
            id={`btn-packager-settings`}
            cypressId={`btn-packager-settings`}
            tooltip="Settings"
          >
            <SettingsIcon style={{ fontSize: '12px', color: 'black' }} />
          </IconButtonWhiteBg>
        </DivFlexHeaderIcons>
      </DivFlexHeader>
    </div>
  );
};

export default PackageHeader;
